/**
 * Pending Invitations Section Component
 *
 * Displays and manages pending organization invitations.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useBundleCatalog, useBundlingEnabled } from '@features/subscriptions';
import { useTableScrollIndicators } from '@hooks/useTableScrollIndicators';
import { Delete, Edit, Send } from '@mui/icons-material';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useEventTracking } from '@services/statsig';
import { getRoleBadgeColor, getRoleDisplayName, ROLES } from '@utils/roleUtils';
import { useSnackbar } from 'notistack';

import {
  useDeleteInvitationMutation,
  useResendInvitationMutation,
  useUpdateInvitationMutation,
} from '../store/api';
import { useInvitationStatus } from '../store/hooks';
import type { OrganizationInvitation } from '../types';

interface PendingInvitationsSectionProps {
  invitations: OrganizationInvitation[];
  onInvitationChange: () => void;
}

export function PendingInvitationsSection({
  invitations,
  onInvitationChange,
}: PendingInvitationsSectionProps) {
  const { enqueueSnackbar } = useSnackbar();
  const { clinicians } = useEventTracking();
  const [deleteInvitation] = useDeleteInvitationMutation();
  const [resendInvitation] = useResendInvitationMutation();
  const [loadingStates, setLoadingStates] = useState<{ [key: string]: boolean }>({});
  const { isBundlingEnabled: showBundling } = useBundlingEnabled();
  const { bundles: catalogBundles, tiers, addOns } = useBundleCatalog();

  // Table scroll indicators
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const { showLeftIndicator, showRightIndicator } = useTableScrollIndicators(tableContainerRef);

  // Edit dialog state
  const [editingInvitation, setEditingInvitation] = useState<OrganizationInvitation | null>(null);

  // Create a map for quick bundle lookup
  const bundleMap = useMemo(() => {
    const map = new Map<string, { name: string; type: 'tier' | 'add_on' }>();
    catalogBundles.forEach(b => {
      map.set(b.bundle_id, { name: b.name, type: b.bundle_type });
    });
    return map;
  }, [catalogBundles]);

  const setLoading = useCallback((invitationId: string, loading: boolean) => {
    setLoadingStates(prev => ({
      ...prev,
      [invitationId]: loading,
    }));
  }, []);

  const handleDelete = useCallback(
    async (invitationId: string) => {
      setLoading(`delete-${invitationId}`, true);
      try {
        await deleteInvitation({ invitationId }).unwrap();
        clinicians.invitationDeleted({ invitation_id: invitationId });
        enqueueSnackbar('Invitation removed successfully', { variant: 'success' });
        onInvitationChange();
      } catch (error: any) {
        enqueueSnackbar(error?.data?.message || 'Failed to remove invitation', {
          variant: 'error',
        });
      } finally {
        setLoading(`delete-${invitationId}`, false);
      }
    },
    [deleteInvitation, enqueueSnackbar, onInvitationChange, setLoading, clinicians],
  );

  const handleResend = useCallback(
    async (invitationId: string) => {
      setLoading(`resend-${invitationId}`, true);
      try {
        await resendInvitation({ invitationId }).unwrap();
        clinicians.invitationResent({ invitation_id: invitationId, success: true });
        enqueueSnackbar('Invitation resent successfully', { variant: 'success' });
        onInvitationChange();
      } catch (error: any) {
        clinicians.invitationResent({ invitation_id: invitationId, success: false });
        enqueueSnackbar(error?.data?.message || 'Failed to resend invitation', {
          variant: 'error',
        });
      } finally {
        setLoading(`resend-${invitationId}`, false);
      }
    },
    [resendInvitation, enqueueSnackbar, onInvitationChange, setLoading, clinicians],
  );

  const handleEdit = useCallback((invitation: OrganizationInvitation) => {
    setEditingInvitation(invitation);
  }, []);

  const handleEditClose = useCallback(() => {
    setEditingInvitation(null);
  }, []);

  const handleEditSuccess = useCallback(() => {
    setEditingInvitation(null);
    onInvitationChange();
  }, [onInvitationChange]);

  if (invitations.length === 0) return null;

  return (
    <>
      <Card
        elevation={1}
        sx={{ borderRadius: 2, overflow: 'hidden', border: 1, borderColor: 'grey.200' }}
      >
        <CardHeader
          title="Pending Invitations"
          titleTypographyProps={{ variant: 'h6', fontWeight: 600 }}
        />
        <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
          <Box
            sx={{
              borderTop: '1px solid',
              borderColor: 'divider',
              overflow: 'hidden',
              position: 'relative',
            }}
          >
            <TableContainer ref={tableContainerRef}>
              <Table>
                <TableHead>
                  <TableRow sx={{ borderBottom: 1, borderColor: 'divider' }}>
                    <TableCell sx={{ bgcolor: 'grey.50', fontWeight: 600, fontSize: '0.875rem' }}>
                      Email
                    </TableCell>
                    <TableCell sx={{ bgcolor: 'grey.50', fontWeight: 600, fontSize: '0.875rem' }}>
                      Role
                    </TableCell>
                    {showBundling && (
                      <TableCell sx={{ bgcolor: 'grey.50', fontWeight: 600, fontSize: '0.875rem' }}>
                        Tier
                      </TableCell>
                    )}
                    {showBundling && (
                      <TableCell sx={{ bgcolor: 'grey.50', fontWeight: 600, fontSize: '0.875rem' }}>
                        Add-ons
                      </TableCell>
                    )}
                    <TableCell sx={{ bgcolor: 'grey.50', fontWeight: 600, fontSize: '0.875rem' }}>
                      Sent
                    </TableCell>
                    <TableCell sx={{ bgcolor: 'grey.50', fontWeight: 600, fontSize: '0.875rem' }}>
                      Status
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{ bgcolor: 'grey.50', fontWeight: 600, fontSize: '0.875rem' }}
                    >
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody
                  sx={{
                    '& tr:last-child td': {
                      borderBottom: 'none',
                    },
                  }}
                >
                  {invitations.map((invitation, index) => (
                    <InvitationRow
                      key={invitation.invitationId}
                      invitation={invitation}
                      index={index}
                      onDelete={handleDelete}
                      onResend={handleResend}
                      onEdit={handleEdit}
                      isDeleting={loadingStates[`delete-${invitation.invitationId}`] || false}
                      isResending={loadingStates[`resend-${invitation.invitationId}`] || false}
                      showBundling={showBundling}
                      bundleMap={bundleMap}
                    />
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            {showLeftIndicator && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  bottom: 0,
                  width: 32,
                  pointerEvents: 'none',
                  background: theme =>
                    `linear-gradient(to right, ${theme.palette.grey[200]}, transparent)`,
                }}
              />
            )}
            {showRightIndicator && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  right: 0,
                  bottom: 0,
                  width: 32,
                  pointerEvents: 'none',
                  background: theme =>
                    `linear-gradient(to left, ${theme.palette.grey[200]}, transparent)`,
                }}
              />
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Edit Invitation Dialog */}
      {editingInvitation && (
        <EditInvitationDialog
          invitation={editingInvitation}
          open={true}
          onClose={handleEditClose}
          onSuccess={handleEditSuccess}
          showBundling={showBundling}
          tiers={tiers}
          addOns={addOns}
        />
      )}
    </>
  );
}

interface InvitationRowProps {
  invitation: OrganizationInvitation;
  index: number;
  onDelete: (invitationId: string) => void;
  onResend: (invitationId: string) => void;
  onEdit: (invitation: OrganizationInvitation) => void;
  isDeleting: boolean;
  isResending: boolean;
  showBundling: boolean;
  bundleMap: Map<string, { name: string; type: 'tier' | 'add_on' }>;
}

function InvitationRow({
  invitation,
  index,
  onDelete,
  onResend,
  onEdit,
  isDeleting,
  isResending,
  showBundling,
  bundleMap,
}: InvitationRowProps) {
  const status = useInvitationStatus(invitation.expiresAt);
  const isExpired = status === 'expired';

  // Only clinicians can have bundles
  const isClinician = invitation.role === ROLES.CLINICIAN;

  // Split bundles into tier and add-ons
  const { tier, addOns } = useMemo((): {
    tier: { id: string; name: string } | null;
    addOns: { id: string; name: string }[];
  } => {
    if (!isClinician || !invitation.bundleIds || invitation.bundleIds.length === 0) {
      return { tier: null, addOns: [] };
    }

    let tierBundle: { id: string; name: string } | null = null;
    const addOnBundles: { id: string; name: string }[] = [];

    invitation.bundleIds.forEach(bundleId => {
      const bundle = bundleMap.get(bundleId);
      if (bundle) {
        if (bundle.type === 'tier') {
          tierBundle = { id: bundleId, name: bundle.name };
        } else {
          addOnBundles.push({ id: bundleId, name: bundle.name });
        }
      }
    });

    return { tier: tierBundle, addOns: addOnBundles };
  }, [isClinician, invitation.bundleIds, bundleMap]);

  const badgeColor = getRoleBadgeColor(invitation.role);

  return (
    <TableRow
      sx={{
        bgcolor: index % 2 === 0 ? 'transparent' : 'grey.50',
      }}
    >
      <TableCell>
        <Typography variant="body2">{invitation.email}</Typography>
      </TableCell>
      <TableCell>
        <Chip
          label={getRoleDisplayName(invitation.role)}
          size="small"
          sx={{
            height: 24,
            fontWeight: 600,
            '& .MuiChip-label': { fontSize: '0.75rem' },
            backgroundColor: theme => {
              if (badgeColor === 'default') return alpha(theme.palette.grey[500], 0.1);
              return alpha(theme.palette[badgeColor].main, 0.1);
            },
            color: theme => {
              if (badgeColor === 'default') return theme.palette.grey[700];
              return theme.palette[badgeColor].dark;
            },
            border: '1px solid',
            borderColor: theme => {
              if (badgeColor === 'default') return theme.palette.grey[500];
              return theme.palette[badgeColor].dark;
            },
          }}
        />
      </TableCell>
      {showBundling && (
        <TableCell>
          {tier ? (
            <Chip
              label={tier.name}
              size="small"
              color="primary"
              variant="outlined"
              sx={{ height: 24, fontWeight: 600, '& .MuiChip-label': { fontSize: '0.75rem' } }}
            />
          ) : (
            <Typography variant="body2" color="text.secondary">
              —
            </Typography>
          )}
        </TableCell>
      )}
      {showBundling && (
        <TableCell>
          {addOns.length > 0 ? (
            <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
              {addOns.map(addOn => (
                <Chip
                  key={addOn.id}
                  label={addOn.name}
                  size="small"
                  variant="outlined"
                  sx={{ height: 24, fontWeight: 600, '& .MuiChip-label': { fontSize: '0.75rem' } }}
                />
              ))}
            </div>
          ) : (
            <Typography variant="body2" color="text.secondary">
              —
            </Typography>
          )}
        </TableCell>
      )}
      <TableCell>
        <Typography variant="body2" color="text.secondary">
          {new Date(invitation.sentAt).toLocaleDateString()}
        </Typography>
      </TableCell>
      <TableCell>
        <Chip
          label={isExpired ? 'Expired' : 'Pending'}
          size="small"
          sx={{
            height: 24,
            fontWeight: 600,
            '& .MuiChip-label': { fontSize: '0.75rem' },
            ...(isExpired
              ? {} // Keep default error color
              : {
                  backgroundColor: theme => alpha(theme.palette.warning.main, 0.1),
                  color: theme => theme.palette.warning.dark,
                  border: '1px solid',
                  borderColor: theme => theme.palette.warning.dark,
                }),
          }}
          {...(isExpired && { color: 'error' })}
        />
      </TableCell>
      <TableCell align="right">
        <Tooltip title="Edit Invitation">
          <span>
            <IconButton size="small" onClick={() => onEdit(invitation)}>
              <Edit fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        {isExpired && (
          <Tooltip title="Resend Invitation">
            <span>
              <IconButton
                size="small"
                onClick={() => onResend(invitation.invitationId)}
                disabled={isResending}
              >
                {isResending ? <CircularProgress size={18} /> : <Send fontSize="small" />}
              </IconButton>
            </span>
          </Tooltip>
        )}
        <Tooltip title="Remove Invitation">
          <span>
            <IconButton
              size="small"
              onClick={() => onDelete(invitation.invitationId)}
              disabled={isDeleting}
            >
              {isDeleting ? <CircularProgress size={18} /> : <Delete fontSize="small" />}
            </IconButton>
          </span>
        </Tooltip>
      </TableCell>
    </TableRow>
  );
}

// Edit Invitation Dialog
interface EditInvitationDialogProps {
  invitation: OrganizationInvitation;
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  showBundling: boolean;
  tiers: Array<{ bundle_id: string; name: string; monthly_unit_amount: number }>;
  addOns: Array<{ bundle_id: string; name: string; monthly_unit_amount: number }>;
}

function EditInvitationDialog({
  invitation,
  open,
  onClose,
  onSuccess,
  showBundling,
  tiers,
  addOns,
}: EditInvitationDialogProps) {
  const { enqueueSnackbar } = useSnackbar();
  const { clinicians } = useEventTracking();
  const [updateInvitation, { isLoading }] = useUpdateInvitationMutation();

  const [selectedRole, setSelectedRole] = useState(invitation.role);
  const [selectedTierId, setSelectedTierId] = useState<string>('');
  const [selectedAddOnIds, setSelectedAddOnIds] = useState<Set<string>>(new Set());

  // Store original bundle selections to restore when switching back to clinician
  const [savedTierId, setSavedTierId] = useState<string>('');
  const [savedAddOnIds, setSavedAddOnIds] = useState<Set<string>>(new Set());

  // Only clinicians can have bundles
  const isClinician = selectedRole === ROLES.CLINICIAN;
  const canSelectBundles = showBundling && isClinician;
  const isSingleTier = tiers.length === 1;

  // Auto-select the single tier when only one is available and none is set
  useEffect(() => {
    if (isSingleTier && tiers[0] && !selectedTierId) {
      setSelectedTierId(tiers[0].bundle_id);
    }
  }, [isSingleTier, tiers, selectedTierId]);

  // Initialize bundle selections from invitation
  useEffect(() => {
    if (invitation.bundleIds && invitation.bundleIds.length > 0) {
      // Find tier and add-ons from the invitation's bundle IDs
      const tierIds = new Set(tiers.map(t => t.bundle_id));
      const addOnIdSet = new Set(addOns.map(a => a.bundle_id));

      let foundTierId = '';
      const foundAddOnIds = new Set<string>();

      invitation.bundleIds.forEach(id => {
        if (tierIds.has(id)) {
          foundTierId = id;
        } else if (addOnIdSet.has(id)) {
          foundAddOnIds.add(id);
        }
      });

      setSelectedTierId(foundTierId);
      setSelectedAddOnIds(foundAddOnIds);
      setSavedTierId(foundTierId);
      setSavedAddOnIds(foundAddOnIds);
    }
  }, [invitation.bundleIds, tiers, addOns]);

  const handleRoleChange = useCallback(
    (newRole: string) => {
      // Save current bundle selections before switching away from clinician
      if (selectedRole === ROLES.CLINICIAN && newRole !== ROLES.CLINICIAN) {
        setSavedTierId(selectedTierId);
        setSavedAddOnIds(selectedAddOnIds);
      }

      // Restore saved bundle selections when switching back to clinician
      if (selectedRole !== ROLES.CLINICIAN && newRole === ROLES.CLINICIAN) {
        setSelectedTierId(savedTierId);
        setSelectedAddOnIds(savedAddOnIds);
      }

      setSelectedRole(newRole);
    },
    [selectedRole, selectedTierId, selectedAddOnIds, savedTierId, savedAddOnIds],
  );

  const handleToggleAddOn = useCallback((bundleId: string) => {
    setSelectedAddOnIds(prev => {
      const next = new Set(prev);
      if (next.has(bundleId)) {
        next.delete(bundleId);
      } else {
        next.add(bundleId);
      }
      return next;
    });
  }, []);

  const handleSave = useCallback(async () => {
    // Collect bundle IDs if bundling is enabled and user is a clinician
    let bundleIds: string[] | null = null;
    if (canSelectBundles) {
      if (!selectedTierId) {
        enqueueSnackbar('Please select a tier', { variant: 'warning' });
        return;
      }
      bundleIds = [selectedTierId, ...Array.from(selectedAddOnIds)];
    } else if (showBundling && !isClinician) {
      // If switching away from clinician, clear bundles
      bundleIds = [];
    }

    const roleChanged = selectedRole !== invitation.role;
    const bundleChanged =
      JSON.stringify(bundleIds || []) !== JSON.stringify(invitation.bundleIds || []);

    // Compute add-on diffs for analytics
    const currentAddOns = new Set(savedAddOnIds);
    const addOnsAdded = Array.from(selectedAddOnIds).filter(id => !currentAddOns.has(id));
    const addOnsRemoved = Array.from(currentAddOns).filter(id => !selectedAddOnIds.has(id));

    // Look up human-readable names for added and removed add-ons
    const addOnNamesAdded = addOnsAdded.map(id => {
      const addOn = addOns.find(a => a.bundle_id === id);
      return addOn?.name || id;
    });
    const addOnNamesRemoved = addOnsRemoved.map(id => {
      const addOn = addOns.find(a => a.bundle_id === id);
      return addOn?.name || id;
    });

    try {
      await updateInvitation({
        invitationId: invitation.invitationId,
        providerUpdateInvitationRequest: {
          role: roleChanged ? selectedRole : undefined,
          bundle_ids: bundleIds,
        },
      }).unwrap();

      clinicians.invitationEdited({
        invitation_id: invitation.invitationId,
        role_changed: roleChanged,
        bundle_changed: bundleChanged,
        from_tier_id: savedTierId || undefined,
        to_tier_id: selectedTierId || undefined,
        addon_ids_added: addOnsAdded,
        addon_ids_removed: addOnsRemoved,
        addon_names_added: addOnNamesAdded,
        addon_names_removed: addOnNamesRemoved,
      });

      enqueueSnackbar('Invitation updated successfully', { variant: 'success' });
      onSuccess();
    } catch (error: any) {
      enqueueSnackbar(error?.data?.message || 'Failed to update invitation', { variant: 'error' });
    }
  }, [
    canSelectBundles,
    selectedTierId,
    selectedAddOnIds,
    selectedRole,
    invitation,
    showBundling,
    isClinician,
    addOns,
    updateInvitation,
    enqueueSnackbar,
    onSuccess,
    clinicians,
  ]);

  const rolesToDisplay = [
    { role: ROLES.CLINICIAN, display_name: 'Clinician' },
    { role: ROLES.OPERATIONS_SUPPORT, display_name: 'Operations Support' },
    { role: ROLES.PRACTICE_ADMINISTRATOR, display_name: 'Practice Administrator' },
    { role: ROLES.SUPERVISOR, display_name: 'Supervisor' },
  ];

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth={"sm"}>
      <DialogTitle>Edit Invitation</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Editing invitation for: <strong>{invitation.email}</strong>
          </Typography>

          <FormControl fullWidth variant="outlined">
            <InputLabel>Role</InputLabel>
            <Select
              value={selectedRole}
              onChange={e => handleRoleChange(e.target.value)}
              label="Role"
              disabled={isLoading}
            >
              {rolesToDisplay.map(role => (
                <MenuItem key={role.role} value={role.role}>
                  {role.display_name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Bundle Selection - only shown when feature is enabled and role is clinician */}
          {canSelectBundles && (
            <>
              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle2" color="text.secondary">
                Subscription Bundles
              </Typography>

              {/* Tier Selection */}
              {isSingleTier ? (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    p: 1.5,
                    bgcolor: 'grey.50',
                    borderRadius: 1,
                  }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {tiers[0].name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    ${(tiers[0].monthly_unit_amount / 100).toFixed(2)}/mo
                  </Typography>
                </Box>
              ) : (
                <FormControl fullWidth variant="outlined" size="small" required>
                  <InputLabel>Tier</InputLabel>
                  <Select
                    value={selectedTierId}
                    onChange={e => setSelectedTierId(e.target.value)}
                    label="Tier"
                    disabled={isLoading}
                  >
                    {tiers.map(tier => (
                      <MenuItem key={tier.bundle_id} value={tier.bundle_id}>
                        <Box
                          sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}
                        >
                          <span>{tier.name}</span>
                          <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
                            ${(tier.monthly_unit_amount / 100).toFixed(2)}/mo
                          </Typography>
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

              {/* Add-ons Selection */}
              {addOns.length > 0 && (
                <FormControl component="fieldset" variant="standard">
                  <FormLabel component="legend" sx={{ fontSize: '0.875rem' }}>
                    Add-ons
                  </FormLabel>
                  <FormGroup>
                    {addOns.map(addOn => (
                      <FormControlLabel
                        key={addOn.bundle_id}
                        control={
                          <Checkbox
                            checked={selectedAddOnIds.has(addOn.bundle_id)}
                            onChange={() => handleToggleAddOn(addOn.bundle_id)}
                            disabled={isLoading}
                            size="small"
                          />
                        }
                        label={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <span>{addOn.name}</span>
                            <Typography variant="caption" color="text.secondary">
                              ${(addOn.monthly_unit_amount / 100).toFixed(2)}/mo
                            </Typography>
                          </Box>
                        }
                      />
                    ))}
                  </FormGroup>
                </FormControl>
              )}
            </>
          )}

          {/* Show message when switching away from clinician */}
          {showBundling && !isClinician && invitation.role === ROLES.CLINICIAN && (
            <Typography variant="body2" color="warning.main">
              Changing role from Clinician will remove any assigned bundles.
            </Typography>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          color="primary"
          variant="contained"
          disabled={isLoading || (canSelectBundles && !selectedTierId)}
          startIcon={isLoading ? <CircularProgress size={16} /> : null}
        >
          {isLoading ? 'Saving...' : 'Save Changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
